/**
 * FrontEnd/my-app/src/crm/portal/PortalLogin.js
 * Passwordless Email OTP Login for Counterparty Portal.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import BASE_URL from '../../url';
import './PortalStyles.css';
import { LuMail, LuShieldCheck, LuArrowRight, LuRefreshCw, LuKeyRound } from 'react-icons/lu';

const PortalLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialEmail = searchParams.get('email') || '';
  const postLoginDest = searchParams.get('dest') || '';

  const [step, setStep] = useState(1); // 1 = enter email, 2 = enter code
  const [email, setEmail] = useState(initialEmail);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const otpInputsRef = useRef([]);

  const getRedirectUrl = useCallback(() => {
    if (!postLoginDest) return '/portal';
    if (postLoginDest.startsWith('/portal')) return postLoginDest;
    if (postLoginDest.startsWith('/')) return `/portal${postLoginDest}`;
    return `/portal/${postLoginDest}`;
  }, [postLoginDest]);

  // Check if session already exists
  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (token) {
      axios.get(`${BASE_URL}/api/portal/session`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      }).then(res => {
        if (res.data.status === 'ok') {
          navigate(getRedirectUrl());
        }
      }).catch(() => {
        localStorage.removeItem('portal_token');
      });
    }
  }, [navigate, getRedirectUrl]);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown(c => c - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // Request Code (Step 1)
  const handleRequestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      const res = await axios.post(`${BASE_URL}/api/portal/auth/request-code`, {
        email: email.trim()
      }, { withCredentials: true });

      setMessage(res.data.message || 'If this email is registered, a code is on its way.');
      setCooldown(res.data.resendInSeconds || 60);
      setStep(2);
      // Auto-focus first OTP input after transitioning
      setTimeout(() => {
        otpInputsRef.current[0]?.focus();
      }, 100);
    } catch (err) {
      // Even on network glitches, show clean message
      setError('Unable to reach server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // OTP Digit Change Handler
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);

    // Auto-advance to next input
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }

    // Auto-submit if all 6 digits entered
    if (value && index === 5 && newDigits.every(d => d !== '')) {
      submitCode(newDigits.join(''));
    }
  };

  // Handle backspace navigation
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  // Handle clipboard paste of full code
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      const digits = pasted.split('');
      setOtpDigits(digits);
      otpInputsRef.current[5]?.focus();
      submitCode(pasted);
    }
  };

  // Verify Code (Step 2)
  const submitCode = async (codeToVerify) => {
    const finalCode = codeToVerify || otpDigits.join('');
    if (finalCode.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${BASE_URL}/api/portal/auth/verify-code`, {
        email: email.trim(),
        code: finalCode
      }, { withCredentials: true });

      if (res.data.status === 'ok') {
        // Save token and session profile
        localStorage.setItem('portal_token', res.data.token);
        localStorage.setItem('portal_user', JSON.stringify({
          contact: res.data.contact,
          company: res.data.company
        }));

        navigate(getRedirectUrl());
      }
    } catch (err) {
      const errData = err.response?.data?.error;
      setError(errData?.message || 'Verification failed. Please check the code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-login-wrap">
      <div className="cp-login-card">
        <div className="cp-login-header">
          <div className="cp-logo-icon" style={{ margin: '0 auto 12px' }}>
            <LuShieldCheck size={22} />
          </div>
          <h1>Counterparty Portal</h1>
          <p>
            {step === 1
              ? 'Secure passwordless login for suppliers and commercial partners.'
              : `Enter the 6-digit code sent to ${email}`}
          </p>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: '6px', fontSize: '13.5px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {message && step === 2 && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
            {message}
          </div>
        )}

        {step === 1 ? (
          /* Step 1: Email Entry */
          <form onSubmit={handleRequestCode}>
            <div className="cp-form-group">
              <label>Work Email Address</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  className="cp-input"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
              </div>
            </div>

            <button type="submit" className="cp-btn-primary" disabled={loading}>
              {loading ? <LuRefreshCw className="spin" size={16} /> : <>Continue with Email <LuArrowRight size={16} /></>}
            </button>
          </form>
        ) : (
          /* Step 2: 6-Digit Code Entry */
          <div>
            <div className="cp-otp-digits" onPaste={handlePaste}>
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="cp-otp-box"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={loading}
                />
              ))}
            </div>

            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => submitCode()}
              disabled={loading || otpDigits.some(d => d === '')}
            >
              {loading ? <LuRefreshCw className="spin" size={16} /> : <><LuKeyRound size={16} /> Verify &amp; Sign In</>}
            </button>

            <div className="cp-resend-bar">
              <button
                type="button"
                className="cp-btn-link"
                onClick={() => {
                  setStep(1);
                  setOtpDigits(['', '', '', '', '', '']);
                  setError('');
                }}
              >
                Change email
              </button>

              <button
                type="button"
                className="cp-btn-link"
                disabled={cooldown > 0 || loading}
                onClick={handleRequestCode}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--cp-border)', marginTop: '28px', paddingTop: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--cp-faint)' }}>
          Prophecy Technologies ERP &copy; {new Date().getFullYear()} · All interactions audited &amp; tamper-evident
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
